/**
 * Responsible for collecting and exporting design data
 * from all steps across both sides of the card
 */
export default class DesignExporter {
    /**
     * Create a new DesignExporter
     * 
     * @param {object} stepController - StepController instance that manages the step handlers
     * @param {object} stepManager - StepManager instance that manages step data
     * @param {object} canvas - STCCanvas instance for image data
     * @param {object} options - Export options
     */
    constructor(stepController, stepManager, canvas, options = {}) {
        this.stepController = stepController;
        this.stepManager = stepManager;
        this.canvas = canvas;
        this.options = {
            includeDataUrls: false, // Whether to include data URLs in export (can be large)
            exportMetadata: true,  // Whether to include metadata in export
            includePreviewImages: false, // Whether to include preview images
            ...options
        };
        
        // Cache for image data to prevent duplicates
        this.imageCache = new Map();
        
        // Store preview images separately
        this.previewImages = {
            front: null,
            back: null
        };
    }
    
    /**
     * Export complete design data
     * 
     * @param {object} templateInfo - Information about the template
     * @param {object} priceInfo - Pricing information
     * @returns {object} The complete design data
     */
    exportDesign(templateInfo, priceInfo) {
        
        this.imageCache.clear();
        
        // Core design data structure
        const designData = {
            version: "2.0", // Increment version for new handler-driven format
            designId: this.generateDesignId(),
            template: {
                id: templateInfo.id,
                title: templateInfo.title
            },
            sides: {
                front: {},
                back: {}
            },
            pricing: {
                basePrice: priceInfo.basePrice,
                extras: priceInfo.totalExtras,
                total: priceInfo.total
            },
            imageReferences: {},
            previewReferences: {},
            // NEW: Handler-driven export metadata
            exportMetadata: {
                handlerDriven: true,
                exportedAt: new Date().toISOString(),
                exportVersion: "2.0"
            }
        };
        
        // Process both sides using handler coordination
        this.processStepsForSideWithHandlers('front', designData.sides.front, designData.imageReferences);
        this.processStepsForSideWithHandlers('back', designData.sides.back, designData.imageReferences);
        
        // Add preview references if they exist
        if (this.previewImages.front || this.previewImages.back) {
            if (this.previewImages.front) {
                designData.previewReferences.front = this.previewImages.front.referenceId;
                if (!designData.imageReferences[this.previewImages.front.referenceId]) {
                    designData.imageReferences[this.previewImages.front.referenceId] = this.previewImages.front;
                }
            }
            
            if (this.previewImages.back) {
                designData.previewReferences.back = this.previewImages.back.referenceId;
                if (!designData.imageReferences[this.previewImages.back.referenceId]) {
                    designData.imageReferences[this.previewImages.back.referenceId] = this.previewImages.back;
                }
            }
        }
        
        return designData;
    }

    /**
     * Process steps for a side with enhanced handler coordination
     *
     * IMPORTANT: Exports ALL handlers (not just completed ones) to preserve all data between edits
     * This ensures that if you edit an item and don't touch a field, it keeps its value
     *
     * @param {string} side - Side name ('front' or 'back')
     * @param {object} stepsContainer - Container to store processed steps
     * @param {object} imageReferenceHolder - Container to store image references
     */
    processStepsForSideWithHandlers(side, stepsContainer, imageReferenceHolder) {
        // Get ALL handlers for this side (not just completed ones)
        const allHandlers = this.stepController.getAllHandlersForSide(side);

        allHandlers.forEach((handler, stepId) => {
            if (!handler) return;

            // Get export data from handler (all handlers export their current state)
            const exportData = handler.getStepExportData();
            stepsContainer[stepId] = exportData;

            // Get image reference if handler provides one
            const imageRef = handler.getImageReferenceForExport();
            if (imageRef) {
                this.tryCacheImageReference(imageRef, imageReferenceHolder);
            }

            // Check if handler has pending save operations
            if (typeof handler.hasSaveableContent === 'function') {
                const stepData = handler.getStepData();
                if (handler.hasSaveableContent(stepData)) {
                    // Mark this step as having content that will need save operations
                    exportData.requiresSaveOperations = true;
                    exportData.saveOperationsPriority = handler.getSaveOperationsPriority();
                }
            }
        });
    }

    /**
     * Process steps for a specific side
     * 
     * @param {string} side - Side name ('front' or 'back')
     * @param {object} stepsContainer - Container to store processed steps
     * @param {object} imageReferenceHolder - Container to store image references
     */
    processStepsForSide(side, stepsContainer, imageReferenceHolder) {
        const completedSteps = Array.from(this.stepManager.completedSteps[side]);
        
        completedSteps.forEach(stepId => {
            const handler = this.stepController.getHandlerForSide(side, stepId);
          
            if(!handler) return;

            stepsContainer[stepId] = handler.getStepExportData();
            this.tryCacheImageReference(handler.getImageReferenceForExport(), imageReferenceHolder);
        });
    }      
    
    /**
     * Set preview images for inclusion in export
     * 
     * @param {object} frontPreview - Front side preview reference
     * @param {object} backPreview - Back side preview reference
     */
    setPreviewImages(frontPreview, backPreview) {
        if (frontPreview) {
            this.previewImages.front = frontPreview;
        }
        
        if (backPreview) {
            this.previewImages.back = backPreview;
        }
    }
    
    /**
     * Export data for a specific side
     * 
     * @param {string} side - 'front' or 'back'
     * @returns {object} The side's design data
     */
    exportSide(side) {
        const completedSteps = Array.from(this.stepManager.completedSteps[side]);
        const sideData = {
            steps: {}
        };
        
        // Process each step in this side
        completedSteps.forEach(stepId => {
            // Get the handler for this step
            const handler = this.stepController.getHandlerForSide(side, stepId);
            
            if (handler) {
                // Get export data from the handler
                sideData.steps[stepId] = handler.getStepExportData();
                
                // If this handler has image data, cache it for later collection
                if (handler.getImageReferenceForExport) {
                    const imageRef = handler.getImageReferenceForExport();
                    if (imageRef && imageRef.referenceId) {
                        this.cacheImageReference(imageRef);
                    }
                }
            } else {
                // Fallback if handler not found - use step data directly
                const stepData = this.stepManager.getStepDataForSide(side, stepId);
                if (stepData) {
                    sideData.steps[stepId] = {
                        stepId,
                        data: stepData,
                        enabled: true
                    };
                }
            }
        });
        
        return sideData;
    }
    
    /**
     * Cache an image reference to avoid duplicates
     * 
     * @param {object} imageRef - Image reference object
     * @param {object} imageReferenceHolder - Container to store image references
     */
    tryCacheImageReference(imageRef, imageReferenceHolder) {
        if (!imageRef || !imageRef.referenceId) return;
        if (imageReferenceHolder[imageRef.referenceId]) return;

        imageReferenceHolder[imageRef.referenceId] = imageRef;
    }
    
    /**
     * Cache an image reference in the image cache
     * 
     * @param {object} imageRef - Image reference object
     */
    cacheImageReference(imageRef) {
        if (!imageRef || !imageRef.referenceId) return;
        this.imageCache.set(imageRef.referenceId, imageRef);
    }
    
    /**
     * Generate a unique design ID
     * 
     * @returns {string} Unique ID
     */
    generateDesignId() {
        return 'design_' + Math.random().toString(36).substring(2, 15) + 
               Math.random().toString(36).substring(2, 15) + 
               '_' + Date.now();
    }
    
    /**
     * Generate a summary of the design for cart/order display
     * 
     * @param {object} designData - The complete design data
     * @returns {object} Design summary
     */
    generateSummary(designData) {
        // Count the number of customizations on each side
        const frontStepCount = Object.keys(designData.sides.front).length;
        const backStepCount = Object.keys(designData.sides.back).length;
        
        // Count images that need processing
        const imageCount = Object.values(designData.imageReferences)
            .filter(img => img.needsProcessing).length;
            
        // Check if we have preview images
        const hasPreviewImages = !!designData.previewReferences && 
            (!!designData.previewReferences.front || !!designData.previewReferences.back);
        
        return {
            templateName: designData.template.title,
            frontCustomizationCount: frontStepCount,
            backCustomizationCount: backStepCount,
            totalCustomizationCount: frontStepCount + backStepCount,
            hasImages: imageCount > 0,
            imageCount: imageCount,
            hasPreviewImages: hasPreviewImages,
            price: {
                base: designData.pricing.basePrice,
                extras: designData.pricing.totalExtras,
                total: designData.pricing.total
            }
        };
    }
    
    /**
     * Create a clean export with no data URLs for server storage
     * 
     * @param {object} designData - Design data
     * @returns {object} Clean design data without data URLs
     */
    createCleanExport(designData) {
        // Create a deep copy
        const cleanData = JSON.parse(JSON.stringify(designData));
        
        // Remove all data URLs from image references
        if (cleanData.imageReferences) {
            Object.keys(cleanData.imageReferences).forEach(key => {
                const imgRef = cleanData.imageReferences[key];
                
                // If URL is a data URL, remove it
                if (imgRef.url && imgRef.url.startsWith('data:')) {
                    imgRef.url = null;
                }
                
                // Remove dataUrl field to save space
                if (imgRef.dataUrl) {
                    delete imgRef.dataUrl;
                }
            });
        }
        
        return cleanData;
    }
}